import { capsuleContact } from "../math/geometry.ts";
import { add, length, scale, sub, v3 } from "../math/vec.ts";
import { Part } from "../skeleton.ts";
import type { FighterState, MatchState } from "../state.ts";
import { TICK_RATE, tuning } from "../tuning.ts";
import { atWall, constrainToCage } from "./cage.ts";
import type { SimEvent } from "./events.ts";
import { walkingInto } from "./movement.ts";

const radius = tuning.movement.radius;
const height = tuning.movement.height;

/**
 * Separates overlapping fighters (as vertical capsules), applies shoves, keeps
 * everyone inside the cage and detects wall crushes. Deterministic pair order.
 */
export function resolveBodies(state: MatchState, events: SimEvent[]): void {
  const fighters = state.fighters;
  const n = fighters.length;
  const crushed = new Array<boolean>(n).fill(false);

  for (let iter = 0; iter < 3; iter += 1) {
    for (let i = 0; i < n; i += 1) {
      const a = fighters[i];
      if (!a?.alive) continue;
      for (let j = i + 1; j < n; j += 1) {
        const b = fighters[j];
        if (!b?.alive) continue;
        const contact = capsuleContact(
          a.pos,
          add(a.pos, v3(0, height, 0)),
          radius,
          b.pos,
          add(b.pos, v3(0, height, 0)),
          radius,
        );
        if (!contact) continue;
        // The one walking into the other takes the correction: a fighter that stands
        // still is a wall, not a puck. Shoves (exhausted, mid-strike) add impulse separately.
        const speedA = length(a.vel);
        const speedB = length(b.vel);
        const shareA = speedA + speedB > 1e-6 ? speedA / (speedA + speedB) : 0.5;
        const dir = v3(contact.normal.x, 0, contact.normal.z);
        a.pos = add(a.pos, scale(dir, contact.depth * shareA));
        b.pos = sub(b.pos, scale(dir, contact.depth * (1 - shareA)));
        if (iter === 0) applyShove(a, b);
      }
    }
    for (let i = 0; i < n; i += 1) {
      const f = fighters[i];
      if (!f?.alive) continue;
      const pushedByWall = constrainToCage(state, f);
      if (iter === 2 && pushedByWall && atWall(state, f) && overlapsAnyone(fighters, i)) {
        crushed[i] = true;
      }
    }
  }

  const crushTicks = Math.round(tuning.cage.crushIntervalSeconds * TICK_RATE);
  for (let i = 0; i < n; i += 1) {
    const f = fighters[i];
    if (!f?.alive) continue;
    if (crushed[i]) {
      f.crushTicks += 1;
      if (f.crushTicks >= crushTicks) {
        f.crushTicks = 0;
        f.hp[Part.Torso] = Math.max(0, (f.hp[Part.Torso] ?? 0) - tuning.cage.crushDamage);
        events.push({ type: "crush", id: f.id, damage: tuning.cage.crushDamage });
      }
    } else {
      f.crushTicks = 0;
    }
  }
}

/** A fighter walking into someone who is exhausted (or mid-strike, later) pushes them. */
function applyShove(a: FighterState, b: FighterState): void {
  if (canBeShoved(b) && walkingInto(a, b)) {
    b.impulse = add(b.impulse, scale(a.vel, tuning.movement.shoveFactor));
  }
  if (canBeShoved(a) && walkingInto(b, a)) {
    a.impulse = add(a.impulse, scale(b.vel, tuning.movement.shoveFactor));
  }
}

function canBeShoved(f: FighterState): boolean {
  return f.exhausted || f.strikeArm !== null || f.strikeLeg !== null;
}

function overlapsAnyone(fighters: FighterState[], i: number): boolean {
  const f = fighters[i];
  if (!f) return false;
  for (let j = 0; j < fighters.length; j += 1) {
    if (j === i) continue;
    const o = fighters[j];
    if (!o?.alive) continue;
    const dx = f.pos.x - o.pos.x;
    const dz = f.pos.z - o.pos.z;
    const min = radius * 2 - 0.01;
    if (dx * dx + dz * dz < min * min) return true;
  }
  return false;
}
